import { useMemo } from 'react'
import type { FarmSubdivision, ToGeoJSON } from '../../types/apiModels'
import { useFarmSubdivisions } from './useFarmSubdivisions'
import { usePaddocks } from './usePaddocks'

export type FarmSubdivisionLayer = {
  name: string
  features: ToGeoJSON[]
}

type PaddockRow = { id: string; toGeoJSON: ToGeoJSON }

const EMPTY_LAYERS: FarmSubdivisionLayer[] = []

const toLayers = (
  subdivisions: FarmSubdivision[],
  paddocks: PaddockRow[],
): FarmSubdivisionLayer[] =>
  [...subdivisions]
    .sort((a, b) => Number(b.year) - Number(a.year))
    .map(subdivision => ({
      name: `Loteo - ${subdivision.year}`,
      features: paddocks
        .filter(paddock => subdivision.paddockIds?.includes(paddock.id))
        .map(paddock => paddock.toGeoJSON),
    }))

export const useFarmSubdivisionsList = (farmId: string | undefined, year?: string | number) => {
  const {
    data: subdivisions,
    isLoading: isLoadingSubdivisions,
    isError: isSubdivisionsError,
    error: subdivisionsError,
  } = useFarmSubdivisions({ farmId, year })
  const {
    data: paddocks,
    isLoading: isLoadingPaddocks,
    isError: isPaddocksError,
    error: paddocksError,
  } = usePaddocks({ farmId })

  const data = useMemo(
    () => (subdivisions && paddocks ? toLayers(subdivisions, paddocks) : EMPTY_LAYERS),
    [subdivisions, paddocks],
  )

  return {
    data,
    isLoading: isLoadingSubdivisions || isLoadingPaddocks,
    isError: isSubdivisionsError || isPaddocksError,
    error: subdivisionsError ?? paddocksError,
  }
}
